import type { FitStatus, SearchFitKind, SearchFitSummary } from './useSearchFit';

const STATUS_LABELS: Record<FitStatus, string> = {
  COMPLETED: 'Match unavailable',
  PARTIAL: 'Match partially scored',
  PENDING: 'Match scoring in progress',
  FAILED: 'Match scoring failed',
  NOT_SCORED: 'Match not yet scored',
  UNAVAILABLE: 'Match unavailable',
  CLOSED: 'Response closed',
  NO_PROVIDERS: 'Fit available after providers are attached',
  BELOW_MIN_PROVIDERS: 'Attach more providers to see a match score',
};
const statusLabel = (summary?: SearchFitSummary) => {
  if (summary?.status !== 'BELOW_MIN_PROVIDERS')
    return STATUS_LABELS[summary?.status ?? 'UNAVAILABLE'];
  const { attachedProviders, minimumProviders } = summary;
  if (typeof attachedProviders !== 'number' || typeof minimumProviders !== 'number')
    return STATUS_LABELS.BELOW_MIN_PROVIDERS;
  const missing = Math.max(1, minimumProviders - attachedProviders);
  return `Attach ${missing} more provider${missing === 1 ? '' : 's'} to see a match score`;
};
// A perfect match scores exactly 1.0 and must read "100%", not "Match unavailable".
const percentage = (score: number | null) =>
  score != null && Number.isFinite(score) && score >= 0 && score <= 1
    ? `${Math.round((score + Number.EPSILON) * 100)}%`
    : null;

export const SearchFit = ({
  summary,
  loading = false,
  error = false,
  onRetry,
  kind = 'job',
  details = false,
}: {
  summary?: SearchFitSummary;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  kind?: SearchFitKind;
  details?: boolean;
}) => {
  const score =
    summary && (summary.status === 'COMPLETED' || summary.status === 'PARTIAL')
      ? percentage(summary.matchScore)
      : null;
  return (
    <div className="my-3 text-sm leading-6 text-content-secondary" data-cy="search-fit">
      {kind === 'b2b' && <p className="font-semibold">Attached-provider match</p>}
      {!error || summary ? (
        loading ? (
          <p>Loading match scores</p>
        ) : (
          <p>
            {score
              ? kind === 'b2b'
                ? `${score} · ${summary?.scoredCount} of ${summary?.attachedCount} providers scored`
                : `${score} match`
              : statusLabel(summary)}
          </p>
        )
      ) : null}
      {error && (
        <p>
          {summary ? 'Unable to refresh match scores' : 'Unable to load match scores'}
          {onRetry && (
            <>
              {' '}
              ·{' '}
              <button
                type="button"
                className="underline"
                onClick={(event) => {
                  event.stopPropagation();
                  onRetry();
                }}
              >
                Retry match scores
              </button>
            </>
          )}
        </p>
      )}
      {details &&
        !loading &&
        summary?.status !== 'CLOSED' &&
        summary?.providers &&
        summary.providers.length > 0 && (
          <ul className="mt-1">
            {summary.providers.map((provider, index) => (
              <li key={provider.responseProviderId}>
                Provider {index + 1}:{' '}
                {provider.status === 'COMPLETED'
                  ? (percentage(provider.matchScore) ?? STATUS_LABELS.UNAVAILABLE)
                  : STATUS_LABELS[provider.status]}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};
